import React from 'react'
import { Form } from 'react-bootstrap'
import FormControl from 'react-bootstrap/FormControl'
import { IMobileIdAuthResponse } from 'auth/interfaces/i-mobileid'
import authenticationService from 'auth/services/authenticationService'
import { getQueryParams } from 'shared/utils/helpers'
import { getReturnUrl } from 'auth/utils/authHelpers'
import { CircleSpinner } from 'shared/components/Spinners'
import { useTranslation } from 'react-i18next'
import {
	formatSsn,
	validatePhoneNumber,
	validateSsn,
} from 'original/utils/validation.helper'
import { CountryCode } from 'libphonenumber-js'
import appConfig from '../../../../app.config'
// @ts-ignore
import ReactPhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { ErrorMessage, logError } from 'shared'
import './mobileIdAuthBlock.scss'
import TermsAndConditionBlock from 'auth/constants/termsAndConditions/termsAndConditionsBlock'

function MobileIdAuthBlock() {
	const { t } = useTranslation()
	const [mobileNumber, setMobileNumber] = React.useState('')
	const [mobileNumberIsValid, setMobileNumberIsValid] = React.useState(true)
	const [personalNumber, setPersonalNumber] = React.useState('')
	const [personalNumberIsValid, setPersonalNumberIsValid] =
		React.useState(true)
	const [error, setError] = React.useState<Error>()
	const [isLoading, setIsLoading] = React.useState(false)
	const [mobileIdAuthData, setMobileIdAuthData] =
		React.useState<IMobileIdAuthResponse>()
	const urlParams = React.useMemo(() => {
		let { ReturnUrl, SigningRequestId } = getQueryParams()
		return { ReturnUrl, SigningRequestId }
	}, [])

	function onMobileNumberChange(
		value: string,
		data: any,
		e: any,
		formattedValue: string,
	) {
		// setMobileNumber((e as unknown) as string)
		setMobileNumber(formattedValue)
	}

	function onPersonalNumberChange(e: React.FormEvent<any>) {
		const value = formatSsn(e.currentTarget.value as string, personalNumber)
		setPersonalNumber(value || '')
	}

	function validatePersonalNumber() {
		const isValid = validateSsn(personalNumber)
		setPersonalNumberIsValid(isValid)
		return isValid
	}

	function validateMobileNumber() {
		const isValid = validatePhoneNumber(
			mobileNumber,
			appConfig.country.toUpperCase() as CountryCode,
			undefined
		)
		setMobileNumberIsValid(isValid)

		return isValid
	}

	function validateAll() {
		const isValidSSN = validatePersonalNumber()
		const isValidMobileNumber = validateMobileNumber()
		return isValidSSN && isValidMobileNumber
	}

	async function mobileIdInitiate() {
		try {
			if (!validateAll()) {
				return
			}

			setIsLoading(true)

			const data = await authenticationService.mobileIdAuthInitiate(
				mobileNumber,
				personalNumber,
				getReturnUrl(),
			)
			setMobileIdAuthData(data)

			const { redirectUrl, secret } =
				await authenticationService.mobileIdAuthComplete(
					data && data.userData,
					t,
				)

			authenticationService.redirectTo(
				redirectUrl || getReturnUrl(),
				secret,
			)
		} catch (error: any) {
			setMobileIdAuthData(undefined)
			logError(error)
			setError(new Error(t('AuthenticationError')))
		} finally {
			setIsLoading(false)
		}
	}

	if (mobileIdAuthData?.userData) {
		return (
			<div className="mobile-id-container">
				<div className="verification-box">
					<p>{t('VerificationCode')}</p>
					<span>{mobileIdAuthData.challengeId}</span>
					<CircleSpinner className="my-3" />
				</div>
				<div className="info-box d-flex">
					<div className="mr-1 tooltip-tip" />
					<span>{t('VerificationCodeToMobile')}</span>
				</div>
			</div>
		)
	}

	if (isLoading) {
		return (
			<div className="mobile-id-container">
				<CircleSpinner />
			</div>
		)
	}

	return (
		<div className="mobile-id-container">
			<div className="login-description mb-4">
				<span>{t('PleaseEnterPhoneMobileId')}</span>
			</div>
			{!urlParams.SigningRequestId && (
				<div>
					<FormControl
						placeholder={t('SSN')}
						className="mt-2"
						name="personalNumber"
						id="personalNumber"
						onChange={onPersonalNumberChange}
						onBlur={validatePersonalNumber}
						isInvalid={!personalNumberIsValid}
						value={personalNumber}
					/>
					<Form.Control.Feedback type="invalid">
						{t('InvalidSsn')}
					</Form.Control.Feedback>
				</div>
			)}
			<div className="mt-2">
				<Form>
					<ReactPhoneInput
						inputProps={{
							name: 'mobileNumber',
							id: 'mobileNumber',
						}}
						containerClass={`react-tel-input ${
							!mobileNumberIsValid ? 'is-invalid' : ''
						}`}
						country={appConfig.country}
						onlyCountries={[appConfig.country]}
						onChange={onMobileNumberChange}
						value={mobileNumber}
						onBlur={validateMobileNumber}
					/>
					<Form.Control.Feedback type="invalid">
						{t('InvalidPhone')}
					</Form.Control.Feedback>
				</Form>
			</div>
			{error && <ErrorMessage message={error.message} css="mt-2" />}
			<button onClick={mobileIdInitiate} className="btn btn-primary mt-4">
				{t('Proceed')}
			</button>
			<TermsAndConditionBlock className="mt-4" />
		</div>
	)
}

export default MobileIdAuthBlock
